import { useState, useRef, useEffect } from "react";
import { ChevronDown, BookmarkIcon, X } from "lucide-react";

export interface FilterFieldConfig {
  key: string;
  label: string;
  options: string[];
}

export interface FiltersBarConfig {
  title?: string;
  fields: FilterFieldConfig[];
  savedViews?: string[];
  savedViewFilters?: Record<string, Record<string, string>>;
}
function FilterDropdown({
  label, options, value, onChange,
}: { label: string; options: string[]; value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative", fontFamily: 'FS Albert, Arial, sans-serif' }}>
      <button
        type="button"
        className="filter-chip"
        style={{
          background: "#F7F6F9",
          border: "1px solid #D4D4D4",
          fontSize: 12,
          fontFamily: 'FS Albert, Arial, sans-serif',
          color: open ? "#6b3fa0" : "#000000",
          borderRadius: 5,
          fontWeight: 400,
          padding: "7px 10px 7px 8px",
          display: "flex",
          alignItems: "center",
          gap: 6,
          minWidth: 120,
          cursor: "pointer",
          outline: "none",
          transition: "color 0.15s, border-color 0.15s, background 0.15s",
        }}
        onClick={() => { setQuery(""); setOpen((v) => !v); }}
        aria-expanded={open}
        aria-haspopup="listbox"
        onMouseEnter={e => { e.currentTarget.style.color = "#6b3fa0"; e.currentTarget.style.borderColor = "#6b3fa0"; }}
        onMouseLeave={e => { e.currentTarget.style.color = open ? "#6b3fa0" : "#000000"; e.currentTarget.style.borderColor = "#D4D4D4"; }}
        onMouseDown={e => { e.currentTarget.style.color = "#6b3fa0"; e.currentTarget.style.borderColor = "#6b3fa0"; }}
        onMouseUp={e => { e.currentTarget.style.color = open ? "#6b3fa0" : "#000000"; e.currentTarget.style.borderColor = open ? "#6b3fa0" : "#D4D4D4"; }}
      >
        <span style={{ fontWeight: 400, color: open ? "#6b3fa0" : "#000", fontSize: 12, transition: "color 0.15s" }}>{label}:</span>
         <span style={{ fontFamily: "FSAlbert-Bold", fontWeight: 700, color: open ? "#6b3fa0" : "#000", fontSize: 12, marginLeft: 4, transition: "color 0.15s" }}>{value}</span>
        <ChevronDown size={14} style={{ marginLeft: 8, color: open ? "#6b3fa0" : "#000", transition: "color 0.15s, transform 0.18s", transform: open ? "rotate(180deg)" : "none" }} />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={label}
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0,
            minWidth: 180, background: "#fff",
            border: "1px solid #D4D4D4", borderRadius: 5,
            boxShadow: "0 8px 20px rgba(0,0,0,0.10)",
            zIndex: 250, maxHeight: 320, overflowY: "auto",
          }}
        >
          {options.length > 12 && <input type="search" aria-label={`Search ${label}`} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Search ${label.toLowerCase()}`} style={{ width: "100%", padding: "8px 14px", border: "none", borderBottom: "1px solid #D4D4D4" }} />}
          {options.filter((option) => option.toLowerCase().includes(query.toLowerCase())).map((opt) => (
            <button
              key={opt}
              type="button"
              role="option"
              aria-selected={opt === value}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "8px 14px", fontSize: 12,
                background: opt === value ? "#F7F6F9" : "transparent",
                color: opt === value ? "#6b3fa0" : "#000000",
                fontWeight: 400,
                border: "none", cursor: "pointer",
                transition: "background 0.15s, color 0.15s",
                fontFamily: 'FS Albert, Arial, sans-serif',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#F7F6F9"; e.currentTarget.style.color = "#6b3fa0"; }}
              onMouseLeave={e => { e.currentTarget.style.background = opt === value ? "#F7F6F9" : "transparent"; e.currentTarget.style.color = opt === value ? "#6b3fa0" : "#000000"; }}
              onMouseDown={e => { e.currentTarget.style.color = "#6b3fa0"; }}
              onMouseUp={e => { e.currentTarget.style.color = opt === value ? "#6b3fa0" : "#000000"; }}
              onClick={() => { onChange(opt); setOpen(false); }}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface Props {
  onFiltersChange?: (filters: Record<string, string>) => void;
  config: FiltersBarConfig;
}

export function FiltersBar({ onFiltersChange, config }: Props) {
  // Use AllFilters for new filter bar
  const dynamicDefaults = config.fields.reduce<Record<string, string>>((acc, field) => {
    acc[field.key] = field.options[0] || "";
    return acc;
  }, {});

  const [filters, setFilters] = useState<Record<string, string>>(dynamicDefaults);
  const [savedOpen, setSavedOpen] = useState(false);
  const savedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (savedRef.current && !savedRef.current.contains(e.target as Node)) setSavedOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const set = (key: string) => (val: string) => {
    const next = { ...filters, [key]: val };
    setFilters(next);
    onFiltersChange?.(next);
  };

  const hasModified = Object.entries(filters).some(([k, v]) => v !== dynamicDefaults[k]);

  const clearAll = () => {
    setFilters(dynamicDefaults);
    onFiltersChange?.(dynamicDefaults);
  };

  const savedViews = config.savedViews || [];

  useEffect(() => {
    const nextDefaults = config.fields.reduce<Record<string, string>>((acc, field) => {
      acc[field.key] = field.options[0] || "";
      return acc;
    }, {});
    setFilters(nextDefaults);
    onFiltersChange?.(nextDefaults);
  }, [config, onFiltersChange]);

  return (
    <div className="filters-bar" role="region" aria-label="Dashboard filters">
      <div className="filters-bar__heading" style={{ fontFamily: 'FSAlbert-Bold', fontWeight: 700, fontSize: 16, color: '#000', marginBottom: 12 }}>{config.title || "Filters"}</div>
      <div className="filters-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start', position: 'relative' }}>
        {config.fields.map((field) => (
          <FilterDropdown
            key={field.key}
            label={field.label}
            options={field.options}
            value={filters[field.key] || ""}
            onChange={set(field.key)}
          />
        ))}

        
      </div>
      <div className="filters-bar__saved" style={{ position: 'absolute', right: '6px', top: '5px', display: 'flex', gap: 8, zIndex: 2 }}>
          {/* Saved Views */}
          {savedViews.length > 0 && <div ref={savedRef} style={{ position: "relative" }}>
            <button
              type="button"
              className="filter-action-btn"
              onClick={() => setSavedOpen((v) => !v)}
              style={{
                background: "#F7F6F9",
                border: "1px solid #D4D4D4",
                fontSize: 12,
                fontFamily: 'FS Albert, Arial, sans-serif',
                color: savedOpen ? "#6b3fa0" : "#000000",
                borderRadius: 5,
                fontWeight: 400,
                padding: "5px 6px",
                display: "flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                outline: "none",
                transition: "color 0.15s, border-color 0.15s, background 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.color = "#6b3fa0"; e.currentTarget.style.borderColor = "#6b3fa0"; }}
              onMouseLeave={e => { e.currentTarget.style.color = savedOpen ? "#6b3fa0" : "#000000"; e.currentTarget.style.borderColor = "#D4D4D4"; }}
              onMouseDown={e => { e.currentTarget.style.color = "#6b3fa0"; e.currentTarget.style.borderColor = "#6b3fa0"; }}
              onMouseUp={e => { e.currentTarget.style.color = savedOpen ? "#6b3fa0" : "#000000"; e.currentTarget.style.borderColor = savedOpen ? "#6b3fa0" : "#D4D4D4"; }}
            >
              <BookmarkIcon size={14} style={{ color: savedOpen ? '#6b3fa0' : '#000' }} /> Saved Views
            </button>
            {savedOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 4px)", right: 0,
                width: 200, background: "#fff",
                border: "1px solid var(--border)", borderRadius: 10,
                boxShadow: "0 8px 20px rgba(0,0,0,0.10)",
                zIndex: 250, overflow: "hidden",
              }}>
                <div style={{ padding: "8px 14px", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", borderBottom: "1px solid var(--border-light)" }}>
                  SAVED VIEWS
                </div>
                {savedViews.map((v) => (
                  <button key={v} type="button" style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "8px 14px", fontSize: 12, color: "var(--text-secondary)",
                    background: "transparent", border: "none", cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-page)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    onClick={() => {
                      const preset = config.savedViewFilters?.[v];
                      if (preset) {
                        const next = { ...dynamicDefaults, ...preset };
                        setFilters(next);
                        onFiltersChange?.(next);
                      }
                      setSavedOpen(false);
                    }}
                  >{v}</button>
                ))}
              </div>
            )}
            </div>}

          {hasModified && (
            <button
              type="button"
              className="filter-action-btn"
              onClick={clearAll}
              style={{
                background: "#F7F6F9",
                border: "1px solid #D4D4D4",
                fontSize: 12,
                fontFamily: 'FS Albert, Arial, sans-serif',
                color: "#000000",
                borderRadius: 5,
                fontWeight: 400,
                padding: "5px 6px",
                display: "flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                outline: "none",
                transition: "color 0.15s, border-color 0.15s, background 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.color = "#6b3fa0"; e.currentTarget.style.borderColor = "#6b3fa0"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "#000000"; e.currentTarget.style.borderColor = "#D4D4D4"; }}
              onMouseDown={e => { e.currentTarget.style.color = "#6b3fa0"; e.currentTarget.style.borderColor = "#6b3fa0"; }}
              onMouseUp={e => { e.currentTarget.style.color = "#000000"; e.currentTarget.style.borderColor = "#D4D4D4"; }}
            >
              <X size={14} style={{ color: '#6b3fa0' }} /> Clear
            </button>
          )}
        </div>
    </div>
  );
}
